// Shared international phone input — a country-code dropdown (flag + dial
// code) plus the national number, emitting a canonical E.164 string. We
// operate in multiple countries, so every phone field in the app uses
// this instead of a bare <input type="tel">. Backed by
// react-phone-number-input (libphonenumber-js) and themed to match the
// app's `.input` styling. See also lib/phone.ts + backend apps/phone.py.
import "react-phone-number-input/style.css";
import PhoneInputBase from "react-phone-number-input";
import type { Country } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  /** ISO-3166 alpha-2 country pre-selected when the value has no `+cc`. */
  defaultCountry?: Country;
  className?: string;
  "aria-label"?: string;
};

export default function PhoneInput({
  value,
  onChange,
  disabled,
  defaultCountry = "FR",
  className = "",
  ...rest
}: Props) {
  return (
    <PhoneInputBase
      className={`nb-phone${disabled ? " nb-phone--disabled" : ""}${
        className ? ` ${className}` : ""
      }`}
      flags={flags}
      international
      // Calling code stays editable (the default) so a user can paste or
      // type a full "+<cc>…" number and the country auto-detects, as well
      // as pick it from the flag dropdown.
      defaultCountry={defaultCountry}
      value={value || undefined}
      // The widget yields E.164 (or undefined when cleared).
      onChange={(v) => onChange(v || "")}
      disabled={disabled}
      // Remaining props (id / name / placeholder / required / aria-label)
      // are forwarded to the underlying <input> by the library.
      {...rest}
    />
  );
}
